import React from 'react';
import styled from 'styled-components';

import type { LayoutApp, LayoutTranslationFn } from '@typings/layout.ts';

import AtriusLogoSVG from '../../../src/images/atrius-logo-standard.svg';
import type { PluginConfig } from '../config-schema.ts';

// z-index higher than map z-index (50)
const Position = styled.div<{ $centered?: boolean }>`
  position: absolute;
  z-index: 51;
  bottom: ${({ $centered }) => ($centered ? '8px' : '12px')};
  inset-inline-start: ${({ $centered }) => ($centered ? '50%' : '14px')};
  transform: ${({ $centered }) => ($centered ? 'translate(-50%, 0)' : 'none')};

  .-mobile & {
    inset-inline-start: 5px;
    bottom: 11px;
  }
`;

const AppInfoButton = styled.button`
  display: flex;
  flex-direction: row;
  background-color: transparent;
  border: none;
  align-items: center;
  user-select: none;
  cursor: pointer;
  height: 24px;
  border-radius: 4px;
  text-shadow:
    1px 1px 1px var(--color-background),
    -1px 1px 1px var(--color-background),
    -1px -1px 1px var(--color-background),
    1px -1px 1px var(--color-background);
  color: var(--color-primaryText);

  &:hover {
    color: blue;
    text-decoration: underline;
  }
`;

const Watermark = styled.div`
  margin-block: 1px 0;
  margin-inline: 0 2px;
  background: url(${AtriusLogoSVG}) no-repeat center center;
  background-size: contain;
  width: 58px;
  height: 14px;
`;

type AppInfoProps = {
  onClick: () => void;
  T: LayoutTranslationFn;
};

const AppInfo = ({ onClick, T }: AppInfoProps) => (
  <AppInfoButton
    id="AppInfoButton"
    data-testid="AppInfo"
    onClick={onClick}
    dir="ltr"
    aria-label={T('appInfo:Atrius Logo')}
  >
    <Watermark />
  </AppInfoButton>
);

function create(app: LayoutApp, config: PluginConfig) {
  const init = () => {};
  const T = app.gt();

  const centered = config.centered || false;
  app.bus.send('layers/register', {
    id: 'appInfo',
    widget: () => (
      <Position className="app-footer" $centered={centered}>
        <AppInfo T={T} onClick={() => app.bus.send('appInfo/watermarkClicked')} />
      </Position>
    ),
    shouldShow: (currentlyShowing: string[]) => {
      if (!app.env.isMobile()) {
        return true;
      }
      return currentlyShowing.includes('headerOnlineHome');
    },
    isOverlay: true,
    layoutId: 'appInfo',
  });

  return {
    init,
  };
}

export { AppInfo, create };
